import React, { useEffect, useState } from "react";
import { styled } from "styled-components";

import missgoUnifiedLogo from "../assets/missgoUnifiedLogo.png";
import Cancel from "../assets/Signup/cancel";
import { useNavigate, useSearchParams } from "react-router-dom";

import MgInput from "../components/input/MgInput";
import { useSession } from "../components/login/SessionProvider";
import { Common } from "../styles/Common";

import { joinViaSmsParams, useJoinViaSms } from "../api/smsVerification/smsVerificationApi";
import { useSmsVerificationInfoStore } from "../store/smsVerificationStore";
import AgreementTermBox from "../components/signUp/AgreementTermBox";
import PartnerCodeBox from "../components/signUp/PartnerCodeBox";

import { transferToNative } from "../lib/WebAppBridge";
import { isString } from "lodash";

const nickNameRegex = /^[가-힣a-zA-Z]{2,10}$/;
const emailRegex = /^[A-Za-z0-9._-]+@[A-Za-z0-9-]+\.[A-Za-z0-9-]+$/;

type CanJoin = {
    isCheckedRequiredTerms: boolean;
    isNicknameValid: boolean;
    isEmailValid: boolean;
};

type ValidationErrorList = { name: string; email: string };

const SignUpBox = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [canJoin, setCanJoin] = useState<CanJoin>({
        isCheckedRequiredTerms: false,
        isNicknameValid: false,
        isEmailValid: false,
    });
    const [joinForm, setJoinForm] = useState<joinViaSmsParams>({
        phone: "",
        email: "",
        name: "",
        isAgreedMarketing: false,
        deviceId: "",
        pushToken: "",
        osType: "",
        partnerCode: "",
    });

    const [validationErrorList, setValidationErrorList] = useState<ValidationErrorList>({
        name: "",
        email: "",
    });

    const { smsVerificationInfo, partnerCode } = useSmsVerificationInfoStore();
    const { setUserSession } = useSession();
    const { mutate: joinViaSms } = useJoinViaSms();

    function convertErrorMessage(message: string): string {
        if (message === "중복된 이메일입니다.") {
            return "이메일이 중복되었습니다.\n문제가 계속되면 미스고로 문의주세요.";
        }
        return message;
    }

    const [errorMessage, setErrorMessage] = useState("");
    const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);

    const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setJoinForm({ ...joinForm, name: value });
        const isValid = nickNameRegex.test(value);
        setValidationErrorList({
            ...validationErrorList,
            name: value.length === 0 ? "" : isValid ? "" : "한글, 영문 2~10자 입력해주세요.",
        });
        setCanJoin({ ...canJoin, isNicknameValid: isValid });
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setJoinForm({ ...joinForm, email: value });
        const isValid = emailRegex.test(value);
        setValidationErrorList({
            ...validationErrorList,
            email: value.length === 0 ? "" : isValid ? "" : "이메일 형식에 맞게 입력해주세요.",
        });
        setCanJoin({ ...canJoin, isEmailValid: isValid });
    };

    const handleJoin = () => {
        if (!joinForm.name) {
            setValidationErrorList((prev) => ({
                ...prev,
                name: "닉네임을 입력해주세요.",
            }));
            return;
        }
        if (!joinForm.email) {
            setValidationErrorList((prev) => ({
                ...prev,
                email: "이메일을 입력해주세요.",
            }));
            return;
        }

        if (!emailRegex.test(joinForm.email)) {
            setValidationErrorList((prev) => ({
                ...prev,
                email: "이메일을 올바르게 입력해주세요.",
            }));
            return;
        }

        if (!canJoin.isCheckedRequiredTerms || !canJoin.isNicknameValid || !canJoin.isEmailValid) {
            return;
        }

        const { partnerCode, isAgreedMarketing, ...requiredFields } = joinForm;

        joinViaSms(joinForm, {
            onSuccess: (data: any) => {
                try {
                    const { success, token } = data || {};
                    if (success && token?.accessToken && token?.refreshToken) {
                        setUserSession({
                            accessToken: token.accessToken,
                            refreshToken: token.refreshToken,
                        });
                        transferToNative(
                            JSON.stringify({
                                accessToken: token.accessToken,
                                refreshToken: token.refreshToken,
                            }),
                            "onRedirectHomeWithToken",
                        );

                        console.log("Bridge Request Success)");
                        return;
                    }
                } catch (e) {
                    console.error("회원가입 성공 처리 중 예외", e);
                }
            },
            onError: (error) => {
                type ApiError = {
                    response?: { data?: { message?: string | string[] } };
                };
                const e = error as ApiError;
                const rawMsg = e?.response?.data?.message;
                const msg = isString(rawMsg)
                    ? convertErrorMessage(rawMsg)
                    : Array.isArray(rawMsg)
                    ? rawMsg.join("\n")
                    : "회원가입 중 오류가 발생했습니다.";
                setErrorMessage(msg);
                setIsOpenErrorDialog(true);
            },
        });
    };

    useEffect(() => {
        if (smsVerificationInfo.phone) {
            setJoinForm((prev) => ({ ...prev, phone: smsVerificationInfo.phone }));
        }
    }, []);

    useEffect(() => {
        const partnerCodeFromQuery = searchParams.get("partnerCode");
        const code = partnerCodeFromQuery || partnerCode || "";

        if (!code) return;

        setJoinForm((prev) => ({ ...prev, partnerCode: code }));
    }, [searchParams, partnerCode]);

    useEffect(() => {
        const agent = navigator.userAgent.toLowerCase();
        if (agent.indexOf("android") > -1) {
            setJoinForm((prev) => ({ ...prev, osType: "android" }));
        } else {
            setJoinForm((prev) => ({ ...prev, osType: "ios" }));
        }
    }, []);

    return (
        <RootBoxStyle>
            <TitleBox>
                <TitleHeader>
                    <img className="logo" alt="미스고 통합 로고" src={missgoUnifiedLogo} width={80} height={36} />
                    <div onClick={() => navigate("/usertype")} style={{ cursor: "pointer" }}>
                        <Cancel />
                    </div>
                </TitleHeader>
                <h3 className="title">미스고 통합 회원가입</h3>
                <SubTitleStyle>
                    가입 시, 미스고 통합회원으로
                    <br />
                    부동산 및 부동산지도를 함께 이용 가능해요.
                </SubTitleStyle>
            </TitleBox>

            <div className="inputBox">
                <div className="flexBox">
                    <p className="nickname">닉네임</p>
                </div>
                <MgInput
                    width={window.innerWidth > 768 ? "336px" : "100%"}
                    value={joinForm.name}
                    onChange={handleNicknameChange}
                    placeholder="한글, 영문 2~10자 입력해주세요."
                />
                {validationErrorList.name && <ErrorTextStyle>{validationErrorList.name}</ErrorTextStyle>}
            </div>

            <div className="inputBox">
                <div className="flexBox">
                    <p className="nickname">이메일</p>
                </div>
                <MgInput
                    width={window.innerWidth > 768 ? "336px" : "100%"}
                    value={joinForm.email}
                    onChange={handleEmailChange}
                    placeholder="missgo@auction.com"
                    type="email"
                />
                {validationErrorList.email && <ErrorTextStyle>{validationErrorList.email}</ErrorTextStyle>}
            </div>

            <AgreementTermBox
                onCheckRequiredTerms={(isChecked) => {
                    setCanJoin({ ...canJoin, isCheckedRequiredTerms: isChecked });
                }}
                onCheckMarketingTerms={(isChecked) => {
                    setJoinForm({ ...joinForm, isAgreedMarketing: isChecked });
                }}
            />

            <PartnerCodeBox
                partnerCode={joinForm.partnerCode}
                setPartnerCode={(code) => setJoinForm({ ...joinForm, partnerCode: code })}
            />

            <BtnStyle
                className={canJoin.isCheckedRequiredTerms && canJoin.isNicknameValid && canJoin.isEmailValid ? "active" : ""}
                onClick={handleJoin}
            >
                가입하기
            </BtnStyle>

            {errorMessage && isOpenErrorDialog && (
                <EmailErrorModal>
                    <EmailErrorModalContent>
                        <p>{errorMessage}</p>
                        <button onClick={() => setIsOpenErrorDialog(false)}>확인</button>
                    </EmailErrorModalContent>
                </EmailErrorModal>
            )}
        </RootBoxStyle>
    );
};

export default SignUpBox;

const TitleHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const RootBoxStyle = styled.div`
    position: fixed;
    transition: all 200ms;
    top: 0;
    height: 100vh;
    overflow-y: scroll;
    background: #ffffff;
    border-radius: 14px;
    width: 100%;
    padding: 24px;
    .flexBox {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .headerBox {
        display: flex;
        gap: 12px;
        height: 40px;
        @media screen and (max-width: 768px) {
            img {
                min-width: 80px;
                max-height: 36px;
            }
        }
    }

    .title {
        margin: 0px 0px 2px 0px;
        font-weight: 700;
        font-size: 24px;
        line-height: 30px;
        color: ${Common.colors.normalTextColor};
    }
    .logo {
        margin-bottom: 8px;
    }
    .checkList {
        margin: 24px 0 10px 0;
        .checkListTitle {
            font-size: 14px;
            font-weight: 500;
            line-height: 18px;
            color: ${Common.colors.normalTextColor};
            margin-top: 33px;
        }

        @media screen and (max-width: 768px) {
            margin: 12px 0 0px 0;
        }
    }
    .inputBox {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin: 24px 0 0 0;
        .nickname {
            font-size: 14px;
        }

        @media screen and (max-width: 768px) {
            margin: 20px 0 0px 0;
        }
    }
`;

const TitleBox = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
`;

const SubTitleStyle = styled.p`
    color: #9da6b3;
    font-size: 12px;
    font-weight: 400;
    line-height: 140%;
    margin-bottom: 24px;
    @media screen and (max-width: 768px) {
        margin-top: 0px;
        margin-bottom: 0px;
    }
`;

const BtnStyle = styled.button`
    margin-top: 24px;
    padding: 12px 14px;
    gap: 10px;
    width: 100%;
    border-radius: 12px;
    background: #c3c3c3;
    border: 1px solid #c3c3c3;
    color: #ffffff;
    font-weight: 400;
    font-size: 18px;
    cursor: pointer;

    &.active {
        background: ${Common.colors.aucMarkerColor};
        border: 1px solid ${Common.colors.aucMarkerColor};
        color: #ffffff;
    }

    @media screen and (max-width: 768px) {
        width: 100%;
        margin-top: 14px;
        margin-bottom: 60px;
    }
`;

const ErrorTextStyle = styled.p`
    color: #ff0000;
    font-size: 12px;
    font-weight: 400;
    line-height: 12px;
    min-height: 18px;
    height: 18px;
`;

const EmailErrorModal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1400;

    @media screen and (max-width: 376px) {
        height: 125vh;
    }
`;

const EmailErrorModalContent = styled.div`
    background-color: white;
    padding: 24px;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    width: 90%;
    max-width: 320px;

    p {
        margin: 0 0 20px;
        font-size: 16px;
        line-height: 1.5;
        color: #333;
        white-space: pre-wrap;
    }

    button {
        padding: 12px 24px;
        border: none;
        border-radius: 8px;
        background-color: #2275ee;
        color: white;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        width: 100%;
        transition: background-color 0.2s;

        &:hover {
            background-color: #1a5cbd;
        }
    }
`;
